import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, fmtMoney, logoSrc, API } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Award, Trophy, Star, Crown, Medal, TrendingUp, Camera, Share2, Trash2, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const money = (v) => fmtMoney(Math.round(Number(v || 0)));

const MEDALS = ["🥇", "🥈", "🥉"];

export default function LeagueTrophies() {
  const { league, teams, isAdmin } = useLeague();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${league.league_id}/history`);
        setHistory(r.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [league.league_id, reloadTick]);

  const doReload = () => setReloadTick(t => t + 1);

  // Normalizzo: se `competitions` è un oggetto {nome: podium[]}, lo converto in array
  // (formato usato dall'import Excel storico → superadmin/import-historical-magnati).
  const normalizedHistory = useMemo(() => history.map(s => {
    const raw = s.competitions;
    let list = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (raw && typeof raw === "object") {
      const EMOJI_MAP = { "Campionato": "🏆", "Coppa dei Magnati": "🥇", "Coppa di Lega": "🥈", "Supercoppa": "👑", "Champions League": "🌟" };
      list = Object.entries(raw).map(([name, podiumOrObj]) => {
        // può essere direttamente array (podium) o oggetto con .positions/.standings
        let positions = [];
        if (Array.isArray(podiumOrObj)) {
          positions = podiumOrObj.map((p, i) => ({
            ...p,
            position: p.position || i + 1,
            team_id: p.team_id || `team_hist_${(p.team_name || "").toLowerCase().replace(/\s+/g, "_")}`,
          }));
        } else if (podiumOrObj) {
          const arr = podiumOrObj.positions || podiumOrObj.standings || [];
          positions = arr.map((p, i) => ({ ...p, position: p.position || i + 1 }));
        }
        return {
          competition_id: name.toLowerCase().replace(/\s+/g, "_"),
          name,
          emoji: EMOJI_MAP[name] || "🏆",
          positions,
        };
      });
    }
    return { ...s, competitions: list };
  }), [history]);

  // Costruisci indice team → { name, logo }
  const teamIndex = useMemo(() => {
    const idx = {};
    for (const t of (teams || [])) idx[t.team_id] = { name: t.name, logo: t.logo_url };
    // arricchisci con nomi trovati in snapshot per team che potrebbero non esistere più
    for (const s of normalizedHistory) {
      for (const t of (s.teams || [])) {
        if (!idx[t.team_id]) idx[t.team_id] = { name: t.team_name, logo: null };
      }
    }
    return idx;
  }, [teams, normalizedHistory]);

  // Aggrega per COMPETIZIONE: {competition_id: {name, emoji, editions:[{season, winner, podium, prize, patrimonio_end}]}}
  const byCompetition = useMemo(() => {
    const map = {};
    for (const s of normalizedHistory) {
      for (const c of (s.competitions || [])) {
        const cid = c.competition_id;
        if (!map[cid]) {
          map[cid] = {
            id: cid,
            name: c.name || cid,
            emoji: c.emoji || "🏆",
            title_awarded: c.title_awarded || null,
            editions: [],
          };
        }
        const positions = (c.positions || []).slice().sort((a,b) => (a.position||99) - (b.position||99));
        const winner = positions.find(p => p.position === 1);
        map[cid].editions.push({
          season: s.season,
          closed_at: s.closed_at,
          winner_photo_url: c.winner_photo_url || null,
          winner: winner ? { ...winner, ...(teamIndex[winner.team_id] || {}) } : null,
          podium: positions.slice(0, 3).map(p => ({ ...p, ...(teamIndex[p.team_id] || {}) })),
        });
      }
    }
    // Ordina per stagione discendente
    for (const cid in map) map[cid].editions.sort((a,b) => b.season - a.season);
    return Object.values(map).sort((a,b) => b.editions.length - a.editions.length);
  }, [normalizedHistory, teamIndex]);

  // Aggrega per TEAM: {team_id: {name, logo, trophies:[{season, competition_name, emoji, title}], count}}
  const byTeam = useMemo(() => {
    const map = {};
    for (const s of normalizedHistory) {
      for (const c of (s.competitions || [])) {
        for (const p of (c.positions || [])) {
          if (p.position === 1) {
            const tid = p.team_id;
            if (!map[tid]) {
              map[tid] = {
                team_id: tid,
                ...(teamIndex[tid] || { name: p.team_name, logo: null }),
                trophies: [],
              };
            }
            map[tid].trophies.push({
              season: s.season,
              competition_id: c.competition_id,
              competition_name: c.name,
              emoji: c.emoji || "🏆",
              title: p.trophy || c.title_awarded || c.name,
              prize: p.prize || 0,
            });
          }
        }
      }
    }
    return Object.values(map).sort((a,b) => b.trophies.length - a.trophies.length);
  }, [normalizedHistory, teamIndex]);

  const totalTrophies = byTeam.reduce((sum, t) => sum + t.trophies.length, 0);
  const totalEditions = byCompetition.reduce((sum, c) => sum + c.editions.length, 0);
  const topTeam = byTeam[0];
  const totalSeasons = normalizedHistory.length;

  if (loading) return <div className="text-slate-500 text-sm py-10 text-center">Caricamento albo d&apos;oro…</div>;

  return (
    <div className="space-y-6">
      {/* Hero header */}
      <div className="border border-amber-500/40 rounded-3xl bg-gradient-to-br from-amber-900/40 via-slate-900 to-slate-950 p-6 sm:p-8 shadow-2xl shadow-black/40 relative overflow-hidden" data-testid="trophy-hero">
        <div className="absolute -right-8 -top-8 text-[220px] opacity-5 select-none">🏆</div>
        <div className="relative z-10">
          <div className="text-[10px] tracking-[0.3em] text-amber-300 uppercase font-black flex items-center gap-2">
            <Award size={14}/> Albo d&apos;Oro della Lega
          </div>
          <h2 className="text-4xl font-black tracking-tight mt-1">🏆 I Trofei di {league.name}</h2>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">
            Vetrina storica dei successi della lega: chi ha alzato la coppa, chi ha dominato, chi ha scritto le pagine più belle.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <HeroKpi emoji="🏆" label="Trofei totali" value={totalTrophies} tone="amber"/>
            <HeroKpi emoji="🎬" label="Edizioni disputate" value={totalEditions} tone="emerald"/>
            <HeroKpi emoji="🗓️" label="Stagioni giocate" value={totalSeasons} tone="indigo"/>
            <HeroKpi emoji="👑" label="Più titolata" value={topTeam?.name || "—"} sub={topTeam ? `${topTeam.trophies.length} trofei` : ""} tone="rose" small/>
          </div>
        </div>
      </div>

      {totalTrophies === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-3xl p-10 text-center" data-testid="trophies-empty">
          <div className="text-6xl mb-3 opacity-50">🏆</div>
          <div className="text-lg font-black text-slate-300 mb-1">Nessun trofeo ancora assegnato</div>
          <div className="text-sm text-slate-500">Applica il saldo di fine stagione in Finanze per iniziare a scrivere la storia della lega.</div>
        </div>
      ) : (
        <>
          {/* Classifica squadre più titolate */}
          <TeamsLeaderboard byTeam={byTeam} />

          {/* Trofei per competizione */}
          <div className="space-y-4">
            {byCompetition.map(comp => (
              <CompetitionShowcase key={comp.id} comp={comp} leagueId={league.league_id} isAdmin={isAdmin} onReload={doReload}/>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function HeroKpi({ emoji, label, value, sub, tone, small }) {
  const toneMap = {
    amber: "border-amber-500/40",
    emerald: "border-emerald-500/40",
    indigo: "border-indigo-500/40",
    rose: "border-rose-500/40",
  };
  return (
    <div className={`rounded-2xl border ${toneMap[tone]} bg-slate-950/60 p-4`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
        <span>{emoji}</span> {label}
      </div>
      <div className={`font-mono font-black mt-1 ${small ? "text-lg truncate" : "text-3xl"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function TeamsLeaderboard({ byTeam }) {
  const chartData = byTeam.slice(0, 10).map(t => ({ name: t.name, Trofei: t.trophies.length }));
  const maxTrophies = Math.max(...byTeam.map(t => t.trophies.length), 1);

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="teams-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
          <Crown size={16} className="text-amber-400"/> 👑 Classifica delle società più titolate
        </h3>
        <span className="text-xs text-slate-500">{byTeam.length} squadre nell&apos;albo</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Podio (top 3) */}
        <div className="lg:col-span-1 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">🥇 Podio storico</div>
          {byTeam.slice(0, 3).map((t, i) => (
            <TeamPodiumCard key={t.team_id} team={t} rank={i}/>
          ))}
        </div>
        {/* Barchart top 10 */}
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">📊 Top 10</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70}/>
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false}/>
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12 }}
                  formatter={(v) => [`${v} trofei`, ""]}/>
                <Bar dataKey="Trofei" radius={[8,8,0,0]} isAnimationActive animationDuration={900}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#FBBF24" : i === 1 ? "#94A3B8" : i === 2 ? "#D97706" : "#334155"}/>
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tabella completa */}
      {byTeam.length > 3 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Squadra</th>
                <th className="text-right px-3 py-2">Trofei</th>
                <th className="text-left px-3 py-2">Ultimi 3</th>
              </tr>
            </thead>
            <tbody>
              {byTeam.map((t, i) => (
                <tr key={t.team_id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`teams-leaderboard-row-${t.team_id}`}>
                  <td className="px-3 py-2 font-black text-slate-500">
                    {i < 3 ? MEDALS[i] : `${i + 1}°`}
                  </td>
                  <td className="px-3 py-2 font-bold">
                    <div className="flex items-center gap-2">
                      {logoSrc(t.logo)
                        ? <img src={logoSrc(t.logo)} alt="" className="w-6 h-6 rounded object-cover border border-slate-700"/>
                        : <span className="w-6 h-6 rounded bg-slate-800 flex items-center justify-center text-[10px]">⚽</span>}
                      {t.name}
                    </div>
                  </td>
                  <td className="text-right px-3 py-2 font-mono font-black text-amber-300">{t.trophies.length}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {t.trophies.slice(-3).map((tr, j) => (
                        <span key={j} title={`${tr.title} · S${tr.season}`}
                          className="text-[10px] font-black px-1.5 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 whitespace-nowrap">
                          {tr.emoji} S{tr.season}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TeamPodiumCard({ team, rank }) {
  const glow = rank === 0 ? "border-amber-400/60 shadow-amber-500/20"
            : rank === 1 ? "border-slate-400/50 shadow-slate-500/10"
            : "border-orange-500/40 shadow-orange-500/10";
  const bg = rank === 0 ? "from-amber-500/20" : rank === 1 ? "from-slate-500/20" : "from-orange-500/20";
  return (
    <div className={`rounded-2xl border ${glow} bg-gradient-to-br ${bg} to-slate-950 p-4 shadow-lg`}>
      <div className="flex items-center gap-3">
        <div className="text-3xl">{MEDALS[rank]}</div>
        {logoSrc(team.logo)
          ? <img src={logoSrc(team.logo)} alt="" className="w-12 h-12 rounded-xl object-cover border border-slate-700"/>
          : <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-xl">⚽</div>}
        <div className="flex-1 min-w-0">
          <div className="font-black text-white text-sm truncate">{team.name}</div>
          <div className="text-xs text-slate-400 font-mono">{team.trophies.length} trofeo{team.trophies.length !== 1 ? "i" : ""}</div>
        </div>
      </div>
    </div>
  );
}

function CompetitionShowcase({ comp, leagueId, isAdmin, onReload }) {
  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 shadow-2xl shadow-black/40 overflow-hidden" data-testid={`competition-showcase-${comp.id}`}>
      <div className="p-5 border-b border-slate-800 bg-gradient-to-r from-amber-500/10 to-transparent flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-3xl shadow-inner">
            {comp.emoji}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">Albo d&apos;oro</div>
            <h3 className="text-xl font-black">{comp.name}</h3>
            {comp.title_awarded && (
              <div className="text-xs text-slate-400 italic">Trofeo: {comp.title_awarded}</div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Edizioni</div>
          <div className="text-3xl font-mono font-black text-amber-300">{comp.editions.length}</div>
        </div>
      </div>

      <div className="p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {comp.editions.map(ed => (
            <EditionCard key={ed.season} ed={ed} comp={comp} leagueId={leagueId} isAdmin={isAdmin} onReload={onReload}/>
          ))}
        </div>
      </div>
    </div>
  );
}

function EditionCard({ ed, comp, leagueId, isAdmin, onReload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const cardUrl = `${API}/leagues/${leagueId}/competitions/${comp.id}/editions/${ed.season}/card.png`;
  const photoUrl = ed.winner_photo_url ? logoSrc(ed.winner_photo_url) : null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/leagues/${leagueId}/competitions/${comp.id}/editions/${ed.season}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("📸 Foto caricata");
      onReload && onReload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removePhoto = async () => {
    if (!window.confirm("Rimuovere la foto di questa edizione?")) return;
    try {
      await api.delete(`/leagues/${leagueId}/competitions/${comp.id}/editions/${ed.season}/photo`);
      toast.success("Foto rimossa");
      onReload && onReload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const shareCard = async () => {
    try {
      const res = await fetch(cardUrl, { credentials: "include" });
      const blob = await res.blob();
      const filename = `trofeo_${comp.name.replace(/\s+/g,'_')}_S${ed.season}.png`;
      // Try Web Share API
      if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: "image/png" })] })) {
        try {
          await navigator.share({
            title: `${ed.winner?.name || ed.winner?.team_name} · ${comp.name} S${ed.season}`,
            text: `🏆 Campione ${comp.name} Stagione ${ed.season}!`,
            files: [new File([blob], filename, { type: "image/png" })],
          });
          return;
        } catch { /* fallback below */ }
      }
      // Fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Card scaricata!");
    } catch (err) { toast.error("Errore generazione card"); }
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 overflow-hidden hover:border-amber-500/30 hover:-translate-y-0.5 transition-all"
      data-testid={`edition-${comp.id}-${ed.season}`}>
      {/* Photo hero (if available) */}
      {photoUrl && (
        <div className="relative h-32 w-full overflow-hidden bg-slate-950 group" data-testid={`edition-photo-${comp.id}-${ed.season}`}>
          <img src={photoUrl} alt={`Vittoria S${ed.season}`} className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"/>
          {isAdmin && (
            <button onClick={removePhoto} title="Rimuovi foto"
              data-testid={`remove-photo-${comp.id}-${ed.season}`}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-slate-950/70 backdrop-blur border border-slate-700 text-slate-300 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={12}/>
            </button>
          )}
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
            Stagione {ed.season}
          </div>
          <div className="text-[10px] text-slate-600">
            {ed.closed_at ? new Date(ed.closed_at).toLocaleDateString("it-IT", { month: "short", year: "numeric" }) : ""}
          </div>
        </div>
        {ed.winner ? (
          <div className="flex items-center gap-3">
            <div className="relative">
              {logoSrc(ed.winner.logo)
                ? <img src={logoSrc(ed.winner.logo)} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-amber-400 shadow-lg shadow-amber-500/30"/>
                : <div className="w-14 h-14 rounded-xl bg-slate-800 border-2 border-amber-400 flex items-center justify-center text-2xl">⚽</div>}
              <div className="absolute -bottom-1 -right-1 text-lg">🏆</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-amber-400 font-black">Campione</div>
              <div className="font-black text-white truncate">{ed.winner.name || ed.winner.team_name}</div>
              {ed.winner.prize > 0 && (
                <div className="text-xs text-emerald-300 font-mono">+{money(ed.winner.prize)} cr</div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-slate-500 text-xs italic">Nessun vincitore registrato</div>
        )}
        {ed.podium && ed.podium.length > 1 && (
          <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2 text-xs">
            {ed.podium.slice(1, 3).map((p, i) => (
              <div key={p.team_id} className="flex items-center gap-1 flex-1 min-w-0">
                <span className="text-sm">{MEDALS[i + 1]}</span>
                {logoSrc(p.logo)
                  ? <img src={logoSrc(p.logo)} alt="" className="w-4 h-4 rounded object-cover"/>
                  : <span className="w-4 h-4 rounded bg-slate-800 text-[8px] flex items-center justify-center">⚽</span>}
                <span className="truncate text-slate-400 text-[10px]">{p.name || p.team_name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Action bar */}
        <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2">
          {ed.winner && (
            <button onClick={shareCard}
              data-testid={`share-card-${comp.id}-${ed.season}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-indigo-500/15 border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/25 font-black text-[10px] uppercase tracking-wider transition-colors">
              <Share2 size={11}/> Card social
            </button>
          )}
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload}/>
              <button onClick={()=>fileRef.current?.click()} disabled={uploading}
                data-testid={`upload-photo-${comp.id}-${ed.season}`}
                title={photoUrl ? "Sostituisci foto" : "Carica foto vincitore"}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-amber-300 hover:border-amber-500/40 font-black text-[10px] uppercase tracking-wider transition-colors">
                <Camera size={11}/> {uploading ? "..." : (photoUrl ? "Cambia" : "Foto")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
